import { PAGE_TYPE_ENUM } from "@shared/types";
import BlogPostContent from "components/blog/blog-post/BlogPostContent";
import BlogPostHeader from "components/blog/blog-post/BlogPostHeader";
import NewsletterSignup from "components/blog/blog-post/NewsletterSignup";
import SocialShare from "components/blog/blog-post/SocialShare";
import { DatoComponentsWrapper } from "components/common/DatoComonentsWrappeer/DatoComponentsWrapper";
import PageLayout from "components/layout/PageLayout";
import { setDYContext } from "config";
import { useLogContext } from "context/log";
import { generatePageId } from "helpers/analyticsLogger";
import { setCachingHeader } from "helpers/cacheHeader";
import { GetServerSideProps } from "next";
import { FC, useEffect } from "react";
import { createDatoClient } from "services/datocms";
import {
  ArticleBySlugQuery,
  ArticleComponentsBySlugQuery,
  getSdk,
} from "services/datocms/generated";
import { createQueryParamsForCaching } from "utils/create-query-params";

interface BlogPostProps {
  article: NonNullable<ArticleBySlugQuery["article"]>;
  articleWithComponents: ArticleComponentsBySlugQuery["article"];
}

export const getServerSideProps: GetServerSideProps<BlogPostProps> = async (
  context,
) => {
  setCachingHeader(context);

  const slug = context?.params?.slug || "";
  const category = context?.params?.category || "";

  try {
    const [
      { article },
      { article: articleWithComponents },
      { allBlogSections },
    ] = await Promise.all([
      getSdk(
        createDatoClient(
          context.preview,
          createQueryParamsForCaching({
            queryType: "ArticleBySlug",
            queryVariables: { slug },
          }),
        ),
      ).ArticleBySlug({ slug: `${slug}` }),
      getSdk(
        createDatoClient(
          context.preview,
          createQueryParamsForCaching({
            queryType: "ArticleComponentsBySlug",
            queryVariables: { slug },
          }),
        ),
      ).ArticleComponentsBySlug({ slug: `${slug}` }),
      getSdk(
        createDatoClient(
          context.preview,
          createQueryParamsForCaching({ queryType: "AllBlogSections" }),
        ),
      ).AllBlogSections(),
    ]);

    if (!article) {
      return {
        notFound: true,
      };
    }

    // Validate that the category and the article are matching
    const isCorrectCategory = allBlogSections.find(
      (section) =>
        section.slug === category && section.slug === article.section?.slug,
    );

    if (!isCorrectCategory) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        article,
        articleWithComponents,
      },
    };
  } catch (error) {
    return {
      notFound: true,
    };
  }
};

const BlogPost: FC<BlogPostProps> = ({ article, articleWithComponents }) => {
  const { useLogPageView } = useLogContext();
  useLogPageView({
    pageType: PAGE_TYPE_ENUM.blog,
    id: generatePageId("blog post"),
  });

  useEffect(() => {
    setDYContext("POST", [article?.id || ""]);
  }, [article?.id]);

  return (
    <PageLayout seo={article.seo} navigationOverlapsContent>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <BlogPostHeader title={article?.title || ""} />
        <BlogPostContent article={article} />
        <SocialShare />
        <NewsletterSignup />
        <DatoComponentsWrapper components={articleWithComponents?.components} />
      </div>
    </PageLayout>
  );
};

export default BlogPost;
